import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DateTime } from 'luxon';

interface AuthStore {
  expiresAt: string | undefined;
  accessToken: string | undefined;
  setAccessToken: (
    token: string | undefined,
    expiresAt: string | undefined,
  ) => void;
  isTokenValid: () => boolean;
}

export const useAuthTokenReseller = create<AuthStore>()(
  persist(
    (set, get) => ({
      accessToken: undefined,
      expiresAt: undefined,
      setAccessToken: (token, expiresAt) => {
        if (!token) {
          set({ accessToken: undefined, expiresAt: undefined });
          return;
        }
        set({ accessToken: token, expiresAt });
      },
      isTokenValid: () => {
        const { expiresAt } = get();
        if (!expiresAt) return false;

        const expires = DateTime.fromISO(expiresAt).toLocal();
        const diff = expires.diffNow().toMillis();
        return diff > 0;
      },
    }),
    {
      name: 'auth-reseller',
      partialize: (state) => ({
        accessToken: state.accessToken,
        expiresAt: state.expiresAt,
      }),
    },
  ),
);

export const useAuthResellerTokenFromUrl = () => {
  const { setAccessToken } = useAuthTokenReseller();

  const handleUrlChange = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const expiresAt = urlParams.get('expires_at');

    if (accessToken || expiresAt) {
      setAccessToken(accessToken || undefined, expiresAt || undefined);

      urlParams.delete('access_token');
      urlParams.delete('expires_at');

      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '');

      window.history.replaceState({}, document.title, newUrl);
    }
  };

  useEffect(() => {
    handleUrlChange();

    const handlePopState = () => {
      handleUrlChange();
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleUrlChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
